import { usePlan } from '../store/planStore.js';
import { weekCoverage, type DaySlot } from './coverage.js';
import { formatUk, isoDate, weekDates, weekdayOf } from '../../domain/calendar.js';
import { useMeals } from '../recipes/useMeals.js';
import { isCustomId } from '../store/library.js';
import type { Weekday } from '../../domain/types.js';
import { Pill } from '../components/Pill.js';

const SHORT_DAY: Record<Weekday, string> = {
  mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat', sun: 'Sun',
};

export function PlanWeek({ onPickMeal }: { onPickMeal: (date: string) => void }): React.JSX.Element {
  const { ready, plan, settings } = usePlan();
  if (!ready) return <p>Loading</p>;

  const dates = weekDates(isoDate(plan.weekStart)).map(String);
  const slots = weekCoverage(plan.dinners, dates);
  const planned = slots.filter((s) => s.kind !== 'empty').length;

  const lunchDaysFor = (date: string): string[] =>
    settings.people
      .filter((p) => p.lunchDays.includes(weekdayOf(isoDate(date))))
      .map((p) => p.name);

  return (
    <section className="plan">
      <header className="plan-head">
        <h1>Week of {formatUk(isoDate(plan.weekStart)).replace(/^\w+ /, '')}</h1>
        <p className="plan-count">{planned} of 7 planned</p>
      </header>

      <ol className="week">
        {slots.map((slot) => (
          <li className="day" key={slot.date}>
            <span className="dow">
              {SHORT_DAY[weekdayOf(isoDate(slot.date))]}
              {lunchDaysFor(slot.date).length > 0 ? <small>nursery</small> : null}
            </span>
            <Slot slot={slot} onPickMeal={onPickMeal} />
          </li>
        ))}
      </ol>
    </section>
  );
}

function Slot({
  slot,
  onPickMeal,
}: {
  slot: DaySlot;
  onPickMeal: (date: string) => void;
}): React.JSX.Element {
  const { byId } = useMeals();
  if (slot.kind === 'continues') {
    return (
      <div className="continues">
        <span className="tie" aria-hidden="true" />
        Same pot &mdash; from {formatUk(isoDate(slot.from)).split(' ')[0]}
      </div>
    );
  }

  if (slot.kind === 'empty') {
    return (
      <button type="button" className="empty" onClick={() => onPickMeal(slot.date)}>
        Nothing planned yet
      </button>
    );
  }

  const meal = byId.get(slot.dinner.mealId);
  return (
    <button
      type="button"
      className="meal"
      data-spans={slot.spans}
      onClick={() => onPickMeal(slot.date)}
    >
      <span className="meal-name">
        {meal?.name ?? (isCustomId(slot.dinner.mealId) ? 'Recipe still syncing' : slot.dinner.mealId)}
      </span>
      <span className="meal-sub">
        {meal ? `${meal.effortMinutes} min` : ''}
        {slot.dinner.spansDays > 1 ? ` · cooks ${slot.dinner.spansDays === 2 ? 'double' : 'a batch'}` : ''}
      </span>
      {meal?.tags.includes('mild') ? <Pill tone="sea">Mild</Pill> : null}
    </button>
  );
}
